import type { CompetitionResult } from './types'
import { formatScheduleDateRange } from './scheduleDates'

/**
 * One Team Finder result flattened for the table view — every value the card
 * shows, resolved to a display string and keyed to its own column.
 *
 * Ids travel alongside the labels so a cell can render its link: the grid needs
 * both "what to show" and "where it goes", and re-deriving the second from the
 * first is how a table ends up linking to the wrong entity.
 */
export interface TeamFinderRow {
  /** The finder-listing id — the grid's row id. */
  id: string
  teamName: string
  teamId?: string
  /** Resolved team logo URL (already through resolveAssetUrl + placeholder). */
  teamLogo: string
  competitionName: string
  scheduleId?: string
  organizationName?: string
  organizationId?: string
  /** Formatted competition run dates, e.g. "Jul 24 – Oct 28, 2026". */
  scheduleDates?: string
  sportName?: string
  /** Division SCHEDULE uid — see `CompetitionResult.divisionId`. */
  divisionId?: string
  divisionName?: string
  genderLabel?: string
  /** "Youth" / "Adult", or undefined when the flag is absent. */
  ageGroup?: string
  divisionAge?: string
  availableDays: string[]
  availableTimes: string[]
  positionNames: string[]
  spotsRemaining?: number
  notes?: string
}

/**
 * Labels a row needs that only the platform can resolve — they come from the
 * Redux type stores (sports, gender types, player types), which `common` must
 * not reach into directly.
 */
export interface ResolvedResultLabels {
  sportName?: string
  genderLabel?: string
  positionNames: string[]
  teamLogo: string
}

/**
 * Flatten one result into its table row. Pure: every store-dependent label is
 * passed in, so this stays testable and platform-agnostic.
 */
export const toTeamFinderRow = (
  result: CompetitionResult,
  labels: ResolvedResultLabels
): TeamFinderRow => ({
  id: result.id,
  teamName: result.teamName,
  teamId: result.teamId,
  teamLogo: labels.teamLogo,
  competitionName: result.competitionName,
  scheduleId: result.scheduleId,
  organizationName: result.organizationName,
  organizationId: result.organizationId,
  scheduleDates:
    formatScheduleDateRange(result.scheduleStartsAt, result.scheduleEndsAt) ||
    undefined,
  sportName: labels.sportName,
  divisionId: result.divisionId,
  divisionName: result.divisionName,
  genderLabel: labels.genderLabel,
  // `isYouth` is a tri-state: true, false, or absent. Only the first two are a
  // fact about the division — absent must stay blank rather than defaulting to
  // "Adult", which would assert something the API never said.
  ageGroup:
    result.isYouth == null ? undefined : result.isYouth ? 'Youth' : 'Adult',
  divisionAge: result.divisionAge,
  availableDays: result.availableDays ?? [],
  availableTimes: result.availableTimes ?? [],
  positionNames: labels.positionNames,
  spotsRemaining: result.spotsRemaining,
  notes: result.notes,
})
